import axios from 'axios';

/**
 * Place lookup for the birth-details form.
 *
 * Queries two OSM-backed geocoders and merges the results:
 *
 *  - Photon (photon.komoot.io) is built for type-ahead and indexes small
 *    settlements well, but returns roads, bus stops and clinics unless the
 *    results are restricted to place:* tags.
 *  - Nominatim is stricter about exact names and sometimes returns a village
 *    Photon misses, but its default search is dominated by POIs and duplicates.
 *
 * Neither alone covered the villages people actually search for, so results are
 * unioned and de-duplicated. Both are free and need no API key; if one fails or
 * times out the other still answers.
 */

// Settlement types worth offering as a birth place. Anything else (roads,
// hospitals, bus stations) is noise for this form.
const PLACE_TAGS = [
  'place:city',
  'place:town',
  'place:village',
  'place:hamlet',
  'place:suburb',
  'place:municipality',
];

// Nominatim asks for an identifying UA; browsers set their own, so this is
// carried in the query string instead where the API allows it.
const REQUEST_TIMEOUT = 8000;

/** Round coordinates so near-identical hits from the two sources collapse. */
function dedupeKey(name, lat, lon) {
  return `${name.toLowerCase()}|${lat.toFixed(3)}|${lon.toFixed(3)}`;
}

/** Build "Village, District, State, Country" from Photon's flat properties. */
function photonLabel(props) {
  const parts = [
    props.name,
    props.district,
    props.county,
    props.city && props.city !== props.name ? props.city : null,
    props.state,
    props.country,
  ];
  // Collapse repeats: Photon often echoes the name as its own district.
  const seen = new Set();
  return parts
    .filter((p) => {
      if (!p || seen.has(p)) return false;
      seen.add(p);
      return true;
    })
    .join(', ');
}

async function searchPhoton(query) {
  const params = new URLSearchParams();
  params.set('q', query);
  params.set('limit', '15');
  PLACE_TAGS.forEach((tag) => params.append('osm_tag', tag));

  const { data } = await axios.get(
    `https://photon.komoot.io/api/?${params.toString()}`,
    { timeout: REQUEST_TIMEOUT }
  );

  return (data?.features || [])
    .filter((f) => Array.isArray(f?.geometry?.coordinates))
    .map((f) => {
      const [lon, lat] = f.geometry.coordinates;
      return {
        display_name: photonLabel(f.properties || {}),
        lat,
        lon,
        kind: f.properties?.osm_value || 'place',
        source: 'photon',
      };
    })
    .filter((r) => r.display_name);
}

async function searchNominatim(query) {
  const params = new URLSearchParams({
    format: 'jsonv2',
    q: query,
    limit: '10',
    addressdetails: '1',
    // Restrict to settlements so clinics and police stations stop crowding out
    // the village the user actually typed.
    featureType: 'settlement',
  });

  const { data } = await axios.get(
    `https://nominatim.openstreetmap.org/search?${params.toString()}`,
    { timeout: REQUEST_TIMEOUT }
  );

  return (data || []).map((p) => ({
    display_name: p.display_name,
    lat: parseFloat(p.lat),
    lon: parseFloat(p.lon),
    kind: p.type || 'place',
    source: 'nominatim',
  }));
}

/**
 * @returns {Promise<Array<{display_name, lat, lon}>>} merged suggestions,
 * best-ranked first. Resolves to [] rather than throwing when both fail, so a
 * flaky network degrades to "no suggestions" instead of an error.
 */
export async function geocodeLocation(query) {
  const trimmed = (query || '').trim();
  if (trimmed.length < 2) return [];

  // allSettled: one provider being down must not hide the other's results.
  const [photonRes, nominatimRes] = await Promise.allSettled([
    searchPhoton(trimmed),
    searchNominatim(trimmed),
  ]);

  if (photonRes.status === 'rejected' && nominatimRes.status === 'rejected') {
    console.error('Geocoding failed:', photonRes.reason, nominatimRes.reason);
    return [];
  }

  const photon = photonRes.status === 'fulfilled' ? photonRes.value : [];
  const nominatim = nominatimRes.status === 'fulfilled' ? nominatimRes.value : [];

  // Interleave so neither provider monopolises the visible list: both order
  // their own results by relevance, so taking them in turn keeps the best of
  // each near the top.
  const merged = [];
  const seen = new Set();
  const max = Math.max(photon.length, nominatim.length);

  for (let i = 0; i < max; i += 1) {
    for (const candidate of [photon[i], nominatim[i]]) {
      if (!candidate) continue;
      if (!Number.isFinite(candidate.lat) || !Number.isFinite(candidate.lon)) continue;
      const key = dedupeKey(candidate.display_name, candidate.lat, candidate.lon);
      if (seen.has(key)) continue;
      seen.add(key);
      merged.push(candidate);
    }
  }

  return merged.slice(0, 12);
}

export default geocodeLocation;
